import React, { Fragment, useEffect, useRef, useState } from "react";
import dayjs from "dayjs";
import {
  cancelAPIRequest,
  classNames,
  convertToGoogleAnalyticsItem,
  generateFileName,
  isLastTokenNull,
  trackAddToCart,
} from "../../Utils";
import { resolveExtendVideoVersion } from "../../Constants/videoCall";
import {
  ArrowDownIcon,
  ChatIcon,
  CheckIcon,
  ChevronRightIcon,
  DocumentIcon,
  DocumentTextIcon,
  PaperClipIcon,
  PhotographIcon,
  PlusIcon,
  ReceiptTaxIcon,
} from "@heroicons/react/outline";
import {
  Popover,
  PopoverButton,
  PopoverPanel,
  Transition,
} from "@headlessui/react";
import {
  addLocalNotification,
  addToCart,
  clearEditMessage,
  clearUpdateConsultation,
  clearUpdateConsultationError,
  editMessage,
  requestListConsultantTemplates,
  requestListMessage,
  requestListNextMessage,
  requestPaymentMethods,
  requestUpdateConsultation,
  resetListTemplateError,
  sendMessage,
  useAuthState,
  useCheckoutDispatch,
  useCheckoutState,
  useConsultantDispatch,
  useConsultantState,
  useConsultationDispatch,
  useConsultationState,
  useNotificationDispatch,
} from "../../Context";
import { Button } from "../Button";
import { Modal } from "../Modal";
import { ChatBubble } from "../ChatBubble";
import { ArrowUpIcon, XIcon } from "@heroicons/react/solid";
import Badge from "../Badge";
import { EmptyList } from "../EmptyList";
import { ErrorList } from "../ErrorList";
import { Alert } from "../Alert";
import awsconfig from "../../aws-exports";
import { isCancelError, uploadData } from "aws-amplify/storage";
import { useNavigate } from "react-router-dom";
import DialogModal from "../DialogModal";
import { useTranslation } from "react-i18next";
import { analytics } from "../../firebase";
import { logEvent } from "firebase/analytics";
import FormLabel from "../FormLabel";
import { ModalConfirm } from "../ModalConfirm";
import InteractiveStarRating from "../InteractiveStarRating";

const defaultTheme = {
  primary: "#2563EB",
  primaryHover: "#1D4ED8",
  primaryLight: "#EFF6FF",
  primaryText: "#FFFFFF",
  background: "#F9FAFB",
  accent: "#BFDBFE",
  accentText: "#1D4ED8",
};

const ChatWindow = ({
  className = "",
  consultation = {},
  renderActions = () => {},
  onJoinCall = undefined,
  theme = defaultTheme,
}) => {
  const { t, i18n } = useTranslation();

  const checkoutDispatch = useCheckoutDispatch();
  const notificationDispatch = useNotificationDispatch();
  const navigate = useNavigate();

  const { user } = useAuthState();
  const { paymentStatus } = useCheckoutState();

  const consultationDispatch = useConsultationDispatch();
  const {
    listMessageRequest,
    sendError,
    sendLoading,
    messages,
    editedMessage,
    messageNextToken: nextToken,
    messageNextLoading: nextLoading,
    messageNextError: nextError,
    messageLoading: loading,
    messageError: error,
    consultationID,
    submitLoading,
    submitError,
  } = useConsultationState();

  const consultantDispatch = useConsultantDispatch();
  const {
    templates,
    templatesLoading,
    templatesError,
    nextTokens,
    nextLoading: templateNextLoading,
  } = useConsultantState();

  const [message, setmessage] = useState("");
  const [replyTo, setreplyTo] = useState({});
  const [editTo, seteditTo] = useState({});
  const [showFileModal, setshowFileModal] = useState(false);
  const [file, setfile] = useState(null);
  const [preview, setpreview] = useState(false);
  const [uploading, setuploading] = useState(false);
  const [uploadingProgress, setuploadingProgress] = useState(0);
  const [showConfirm, setshowConfirm] = useState(false);
  const [currentUpload, setcurrentUpload] = useState(null);
  const [page, setPage] = useState(1);
  const [fileUploadButtonRef, setfileUploadButtonRef] = useState(null);
  const [review, setreview] = useState("");
  const [rating, setrating] = useState(0);

  const [showReviewModal, setshowReviewModal] = useState(false);
  const [showConsultantReviewModal, setshowConsultantReviewModal] =
    useState(false);
  const [showSuccessModal, setshowSuccessModal] = useState(false);

  const loadMoreRef = useRef(null);
  const endMessageRef = useRef(null);
  const lastHandledMessageRef = useRef(null);
  const messagesRef = useRef({});
  const messageListRef = useRef(null);
  const inputRef = useRef(null);

  // Required for scrolling
  const [messagesRequireRef, setmessagesRequireRef] = useState({});
  const [pulseMessage, setpulseMessage] = useState("");
  const [imagesLoaded, setimagesLoaded] = useState({});
  const [firstScroll, setfirstScroll] = useState({});
  const [endOfScroll, setendOfScroll] = useState(9999);
  const [isLoadMore, setisLoadMore] = useState(false);
  const [showNewMessageButton, setshowNewMessageButton] = useState(false);
  const [showReplyAfterFinished, setshowReplyAfterFinished] = useState(false);

  // Load all messages
  useEffect(() => {
    listMessages();

    // Reset reply after finished state when consultation changes
    setshowReplyAfterFinished(false);

    const silentUpdate = setInterval(() => {
      listMessages(true);
    }, 30000);

    return () => {
      if (consultation?.id) {
        setfirstScroll({
          [consultation.id]: false,
        });
      }
      setimagesLoaded({});
      setisLoadMore(false);
      clearInterval(silentUpdate);
    };
    // Depend on the id, not the object: consultation refetches produce fresh
    // object identities with the same id, and re-running this effect resets
    // firstScroll, which force-scrolls the chat to the bottom.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consultation?.id]);

  function listMessages(silent = false) {
    if (listMessageRequest) {
      cancelAPIRequest(listMessageRequest);
    }
    if (consultation?.id) {
      requestListMessage(consultationDispatch, { id: consultation.id, silent });
    }
  }

  // Update scroll position
  useEffect(() => {
    if (messageListRef.current) {
      const height = messageListRef.current.offsetHeight;
      const scrollTop = messageListRef.current.scrollTop;
      setendOfScroll(height + scrollTop);
    }
  }, [messageListRef]);

  // Get price for consultation extend
  useEffect(() => {
    if (!paymentStatus?.prices) {
      requestPaymentMethods(checkoutDispatch);
    }
  }, [checkoutDispatch, paymentStatus]);

  // Send message error handling
  useEffect(() => {
    if (sendError) {
      let message = "Error send message.";
      if (sendError?.message) {
        message = sendError.message;
      }

      addLocalNotification(notificationDispatch, {
        id: `sendError-${Math.floor(Date.now() / 1000)}`,
        type: "error",
        title: "Error",
        body: message,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sendError]);

  // Remove file preview
  useEffect(() => {
    if (!file) {
      setpreview(null);
      return;
    }

    if (file.type !== "image/png" && file.type !== "image/jpeg") {
      setpreview(null);
      return;
    }

    const objectUrl = URL.createObjectURL(file);
    setpreview(objectUrl);

    return () => URL.revokeObjectURL(objectUrl);
  }, [file]);

  // Auto scroll when messages changed
  useEffect(() => {
    let messagesHasImage = [];
    let messagesHasReply = [];

    messages.forEach((message) => {
      if (message.imageUrl) {
        messagesHasImage.push(message.id);
      }
      if (message.replyTo) {
        messagesHasReply.push(message);
        if (message.replyTo.imageUrl) {
          messagesHasImage.push(`${message.id}-${message.replyTo.id}`);
        }
      }
    });

    let hasImage = {};
    messagesHasImage.forEach((messageId) => {
      hasImage = {
        ...hasImage,
        [messageId]: imagesLoaded[messageId] || false,
      };
    });

    let allLoaded = allImagesLoaded(hasImage);
    if (messagesHasImage.length === 0) {
      allLoaded = true;
    }

    if (allLoaded) {
      setisLoadMore(false);
    } else {
      setimagesLoaded(hasImage);
    }

    let requireRef = {};
    messagesHasReply.forEach((message) => {
      if (message.replyTo?.id) {
        requireRef = {
          ...requireRef,
          [message.replyTo.id]: message.replyTo,
        };
      }
    });
    setmessagesRequireRef(requireRef);

    if (messages.length > 0) {
      const isAuthorMessage = messages[messages.length - 1].user.id === user.id;
      scrollToBottom(allLoaded, isAuthorMessage, "inside");
    }

    inputRef.current?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages]);

  // Auto scroll when all images loaded
  useEffect(() => {
    if (messages.length > 0 && Object.keys(imagesLoaded).length > 0) {
      const allLoaded = allImagesLoaded(imagesLoaded);
      const isAuthorMessage =
        messages[messages.length - 1].user?.id === user.id;
      scrollToBottom(allLoaded, isAuthorMessage, "image");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imagesLoaded]);

  // Pulse when replied message clicked
  useEffect(() => {
    if (pulseMessage !== "") {
      setTimeout(() => {
        setpulseMessage("");
      }, 2000);
    }
  }, [pulseMessage]);

  // Remove new message notification when scrolled to the bottom
  useEffect(() => {
    if (
      endOfScroll >= endMessageRef.current?.offsetTop &&
      showNewMessageButton
    ) {
      setshowNewMessageButton(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endOfScroll]);

  useEffect(() => {
    if (showFileModal && fileUploadButtonRef) {
      fileUploadButtonRef.focus();
    }
  }, [showFileModal, fileUploadButtonRef]);

  useEffect(() => {
    const handlePaste = (event) => {
      const items = event.clipboardData.items;
      if (inputRef.current) {
        for (let i = 0; i < items.length; i++) {
          const item = items[i];
          if (item.type.indexOf("image") !== -1) {
            const blob = item.getAsFile();

            if (blob.size < 4e6) {
              const reader = new FileReader();

              const fileName = extractFileName(blob);

              reader.onloadend = () => {
                setpreview(reader.result);
                setfile(new File([blob], fileName, { type: blob.type }));
                setshowFileModal(true);
              };

              reader.readAsDataURL(blob);
              break;
            } else {
              addLocalNotification(notificationDispatch, {
                id: "chat-file-limit-image",
                type: "error",
                title: "File size limit exceeded",
                body: "File size should be less than 4MB.",
              });
            }

            break;
          }
        }
      }
    };

    window.addEventListener("paste", handlePaste);

    return () => {
      window.removeEventListener("paste", handlePaste);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (consultationID) {
      setshowReviewModal(false);
      setshowSuccessModal(true);
    }
  }, [consultationID]);

  useEffect(() => {
    if (consultation?.rating && showReviewModal) {
      setrating(consultation.rating);
      setreview(consultation.review);
    } else {
      resetReviewModal();
    }
  }, [consultation, showReviewModal]);

  const handleFileUploadButtonRef = (ref) => {
    setfileUploadButtonRef(ref);
  };

  useEffect(() => {
    if (editTo?.text) {
      setmessage(editTo.text);
    }
  }, [editTo.text]);

  useEffect(() => {
    if (editedMessage?.id) {
      seteditTo({});
      setmessage("");
      clearEditMessage(consultantDispatch);
    }
  }, [editedMessage, consultantDispatch]);

  const extractFileName = (file) => {
    const timestamp = new Date().getTime();
    const newFileName = file.name.split(".").slice(0, -1);
    return `${newFileName}_${timestamp}.${file.name.split(".").pop()}`;
  };

  const getConsultantTemplate = () => {
    requestListConsultantTemplates(consultantDispatch, {
      page: 1,
      limit: 10,
    });
  };

  function onScroll(event) {
    setendOfScroll(
      event.currentTarget.scrollTop + event.currentTarget.offsetHeight,
    );
  }

  function scrollToBottom(allLoaded, isAuthorMessage, source) {
    const scrolled = firstScroll[consultation.id] || false;
    // The 30s silent poll re-dispatches the same list with fresh object
    // identities, so this runs without any new message — only the first
    // render of a given newest message may scroll or raise the button.
    const lastMessageId = messages[messages.length - 1]?.id;
    const isNewMessage =
      !!lastMessageId && lastMessageId !== lastHandledMessageRef.current;

    if (allLoaded && endMessageRef.current && messageListRef.current) {
      if (!isLoadMore) {
        if (!scrolled || (isAuthorMessage && isNewMessage)) {
          if (
            endMessageRef.current.offsetTop >
            messageListRef.current.offsetHeight
          ) {
            endMessageRef.current.scrollIntoView({ behavior: "smooth" });
          }
          setfirstScroll({
            [consultation.id]: true,
          });
        } else if (isNewMessage) {
          if (endOfScroll < endMessageRef.current.offsetTop) {
            setshowNewMessageButton(true);
          }
        }
        lastHandledMessageRef.current = lastMessageId;
        setisLoadMore(false);
      }
    }
  }

  const onImageLoad = (messageId) => {
    setimagesLoaded((prevData) => ({
      ...prevData,
      [messageId]: true,
    }));
  };

  const allImagesLoaded = (images = {}) => {
    let loaded = true;
    const allKeys = Object.keys(images);
    for (let index = 0; index < allKeys.length; index++) {
      const key = allKeys[index];
      if (!images[key]) {
        loaded = false;
        break;
      }
    }
    return loaded;
  };

  function onSubmit() {
    if (message.trim() !== "") {
      if (editTo.id) {
        const input = {
          id: editTo.id,
          text: message,
        };
        editMessage(consultationDispatch, input);
      } else {
        const input = {
          id: consultation.id,
          userID: user.id,
          text: message,
        };
        if (replyTo.id) {
          input.replyTo = JSON.stringify(replyTo);
        }
        sendMessage(consultationDispatch, input);
        setmessage("");
        adjustTextareaHeight();
        setreplyTo({});
        inputRef.current.style.height = "38px";
      }
    }
  }

  const onSubmitReview = () => {
    if (rating > 0) {
      const input = {
        id: consultation.id,
        rating,
        review,
      };
      requestUpdateConsultation(consultationDispatch, { input });
    }
  };

  const onProgress = (progress) => {
    const { transferredBytes, totalBytes } = progress;
    setuploadingProgress(transferredBytes / totalBytes);
  };

  const onUploadFile = async () => {
    if (file?.name && consultation?.id) {
      try {
        setuploading(true);

        const key = `chat/${consultation.id}/${file.name}`;

        const upload = uploadData({
          path: ({ identityId }) => `private/${identityId}/${key}`,
          data: file,
          options: {
            contentType: file.type,
            onProgress,
          },
        });

        setcurrentUpload(upload);

        const result = await upload.result;

        setshowConfirm(false);

        const input = {
          id: consultation.id,
          userID: user.id,
        };

        if (result.path) {
          if (file.type === "image/png" || file.type === "image/jpeg") {
            input.image = {
              key,
              bucket: awsconfig.aws_user_files_s3_bucket,
              region: awsconfig.aws_user_files_s3_bucket_region,
            };
          } else {
            input.document = {
              key,
              bucket: awsconfig.aws_user_files_s3_bucket,
              region: awsconfig.aws_user_files_s3_bucket_region,
            };
          }
        }

        if (replyTo.id) {
          input.replyTo = JSON.stringify(replyTo);
        }

        if (consultation?.id) {
          sendMessage(consultationDispatch, input);
          setshowFileModal(false);
          URL.revokeObjectURL(preview);
          setmessage("");
          setreplyTo({});
          setfile(null);
          setpreview(null);
        }
      } catch (error) {
        let errorMessage = "Error uploading file.";
        let errorType = "error";
        if (error?.message) {
          errorMessage = error.message;
        }
        if (isCancelError(error)) {
          errorMessage = "Upload canceled.";
          errorType = "warning";
        }
        addLocalNotification(notificationDispatch, {
          id: `uploadError-${Math.floor(Date.now() / 1000)}`,
          type: errorType,
          title: "Error",
          body: errorMessage,
        });
      } finally {
        setuploading(false);
        setuploadingProgress(0);
      }
    }
  };

  const onExtend = () => {
    const products = [];

    const consultationProduct = {
      id: consultation.id,
      type: "consultation-extend",
      name: t("Consultation Extension"),
      detail: {
        consultantID: consultation.consultant.id,
        categoryID: consultation?.category?.id || "general",
        categoryName: consultation?.category?.name || "Pajak Pusat secara Umum",
        description: consultation?.description?.description,
      },
      price: consultation.consultant?.consultationPrice
        ? consultation.consultant?.consultationPrice
        : paymentStatus?.prices?.["online-consultation"]?.price,
      quantity: 1,
    };

    if (consultation.useGlobalBalance && user.callBalance >= 60) {
      consultationProduct.detail.useGlobalBalance = true;
      consultationProduct.detail.globalBalance = user.callBalance;
    }

    products.push(consultationProduct);

    if (consultation.callEnabled && !consultation.useGlobalBalance) {
      const { versionId, provider, callType, available } =
        resolveExtendVideoVersion(
          consultation,
          paymentStatus?.prices,
          consultation.consultant?.acceptCall
        );
      const isAudio = callType === "audio";

      products.push({
        // Audio extends keep the `video-` prefix: checkout, processTransaction
        // and the voucher check all key off `'video-' in id`.
        id: `video-${consultation.id}-${versionId}`,
        type: "consultation-extend",
        name: isAudio
          ? t("Audio Call Add-On Extension")
          : t("Video Call Add-On Extension"),
        detail: {
          consultantID: consultation.consultant.id,
          categoryID: consultation?.category?.id || "general",
          categoryName:
            consultation?.category?.name || "Pajak Pusat secara Umum",
          description: consultation?.description?.description,
          videoVersion: versionId,
          videoProvider: provider,
          callType,
          unavailable: !available,
        },
        price: isAudio
          ? consultation.consultant?.audioPrice ||
            paymentStatus?.prices?.[versionId]?.price
          : consultation.consultant?.callPrice
            ? consultation.consultant?.callPrice
            : paymentStatus?.prices?.[versionId]?.price,
        quantity: 1,
      });
    }

    const analyticsParams = {
      currency: "IDR",
      value: products.reduce((total, product) => {
        return total + product.price * product.quantity;
      }, 0),
      items: products.map((product) => convertToGoogleAnalyticsItem(product)),
    };

    logEvent(analytics, "add_to_cart", analyticsParams);
    trackAddToCart({ products });

    addToCart(checkoutDispatch, {
      products,
    });

    navigate("/checkout");
  };

  function loadMoreMessages() {
    if (nextToken) {
      requestListNextMessage(consultationDispatch, {
        id: consultation.id,
        nextToken,
      });
    }
  }

  const renderFileModal = () => (
    <Modal
      title="Send File"
      show={showFileModal}
      onClose={() => {
        setshowFileModal(false);
        if (!uploading) {
          setfile(null);
        } else {
          setshowConfirm(true);
        }
      }}
      footer={
        <div className="p-4 flex flex-row-reverse gap-2 justify-between items-center border-t border-gray-300 bg-gray-50 rounded-b-lg">
          <button
            ref={handleFileUploadButtonRef}
            disabled={uploading}
            onClick={onUploadFile}
            className="w-24 shrink-0 inline-flex justify-center items-center whitespace-nowrap px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2 disabled:bg-slate-100 disabled:text-slate-400 disabled:cursor-not-allowed"
            style={
              uploading
                ? {}
                : { backgroundColor: theme.primary, color: theme.primaryText }
            }
          >
            {uploading ? (
              <svg
                role="status"
                className="w-5 h-5 animate-spin text-slate-400"
                viewBox="0 0 100 101"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path
                  d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                  fill="#E5E7EB"
                />
                <path
                  d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                  fill="currentColor"
                />
              </svg>
            ) : (
              t("Send File")
            )}
          </button>
          {file?.name && (
            <div className="flex-1 overflow-hidden">
              {uploading ? (
                <div className="w-full">
                  <p className="text-xs text-slate-500">{t("Uploading")}</p>
                  <div className="w-full mt-1 bg-gray-200 rounded-md sm:rounded-lg dark:bg-gray-700">
                    <div
                      className="text-xs font-medium text-center p-0.5 leading-none rounded-full"
                      style={{
                        backgroundColor: theme.primary,
                        color: theme.primaryText,
                        width: `${Math.round(uploadingProgress * 100)}%`,
                      }}
                    >
                      {Math.round(uploadingProgress * 100)}%
                    </div>
                  </div>
                </div>
              ) : (
                <div className="w-full">
                  <p className="text-xs text-slate-500 truncate">
                    {file?.name}
                  </p>
                  <p className="text-xs font-medium">
                    {Math.round((file?.size || 0) / 1000)} KB
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      }
    >
      <div className="bg-white p-4">
        <div className="flex justify-center">
          {preview ? (
            <img src={preview} className="w-96" alt="preview" />
          ) : (
            <DocumentIcon className="h-24 w-24 text-slate-500" />
          )}
        </div>
      </div>
    </Modal>
  );

  const renderCancelUpload = () => (
    <DialogModal
      show={showConfirm}
      type="warning"
      title="Cancel Uploading?"
      body="Are you sure to cancel uploading? This action cannot be undone."
      onCancel={() => {
        setshowFileModal(uploading);
      }}
      cancelTitle="Cancel"
      onClose={() => {
        setshowConfirm(false);
      }}
      confirmTitle="Yes"
      onConfirm={() => {
        if (currentUpload) {
          currentUpload.cancel();
        }
      }}
    />
  );

  const resetReviewModal = () => {
    setreview("");
    setrating(0);
  };

  const renderReviewModal = () => (
    <Modal
      title={consultation?.rating ? "Change Review" : "Rate and Review"}
      show={showReviewModal}
      onClose={() => {
        setshowReviewModal(false);
        resetReviewModal();
        clearUpdateConsultation(consultationDispatch);
        clearUpdateConsultationError(consultationDispatch);
      }}
      footer={
        <div className="p-4 flex flex-row-reverse gap-2 justify-between items-center border-t border-gray-300 bg-gray-50 rounded-b-lg">
          <Button
            loading={submitLoading}
            disabled={submitLoading || !rating}
            onClick={onSubmitReview}
            className="flex-1"
            title="Send"
          />
          <Button
            loading={submitLoading}
            disabled={submitLoading}
            onClick={() => {
              resetReviewModal();
              setshowReviewModal(false);
              clearUpdateConsultationError(consultationDispatch);
            }}
            className="flex-1"
            title="Cancel"
            outline
          />
        </div>
      }
    >
      <div className="bg-white p-4 flex flex-col gap-4">
        {submitError && (
          <Alert
            title="Error"
            type="error"
            message={submitError?.message || "submitError Review."}
            errors={submitError?.errors}
          />
        )}
        <InteractiveStarRating
          title="Tap to rate"
          initialRating={rating}
          onRatingChange={(rating) => setrating(rating)}
        />

        <div className="flex flex-col gap-2">
          <FormLabel label={t("Tell us more")} />
          <textarea
            id="review-textarea"
            placeholder={t("Your experience during the consultation")}
            className="flex-1 resize-none text-sm rounded-md shadow-sm border-gray-300 focus:border-blue-500 focus:ring-blue-500"
            value={review}
            onChange={(e) => setreview(e.target.value)}
            rows={5}
          />
        </div>
      </div>
    </Modal>
  );

  const renderConsultanReviewModal = () => (
    <Modal
      title="Review"
      show={showConsultantReviewModal}
      onClose={() => {
        setshowConsultantReviewModal(false);
      }}
      footer={
        <div className="p-4 flex flex-row-reverse gap-2 justify-between items-center border-t border-gray-300 bg-gray-50 rounded-b-lg">
          <Button
            loading={submitLoading}
            disabled={submitLoading}
            onClick={() => {
              setshowConsultantReviewModal(false);
            }}
            className="flex-1"
            title="Close"
            outline
          />
        </div>
      }
    >
      <div className="bg-white p-4 flex flex-col gap-4">
        <div className="flex flex-col gap-4">
          <div className="flex justify-between text-sm">
            <span className="font-medium text-gray-700">
              {consultation?.description?.name}
            </span>
            <span className="text-gray-400">
              {dayjs(consultation.updatedAt)
                .locale(i18n.language)
                .format("D MMM YYYY")}
            </span>
          </div>
          <InteractiveStarRating
            initialRating={consultation.rating}
            disabled
            position="left"
          />
          {consultation.review && (
            <p className="text-sm text-gray-700">{consultation.review}</p>
          )}
        </div>
      </div>
    </Modal>
  );

  const fillTemplate = (templateText) => {
    inputRef.current.value = templateText;
    setmessage(templateText);
    adjustTextareaHeight();
  };

  function adjustTextareaHeight() {
    const textarea = inputRef.current;
    textarea.style.height = "38px";
    textarea.style.height = `${textarea.scrollHeight}px`;
  }

  function handleKeyDown(event) {
    if (event.keyCode === 13 && !event.shiftKey) {
      if (message) {
        event.preventDefault();
        onSubmit();
      }
    }
  }

  function handleInput() {
    adjustTextareaHeight();
  }

  const onLoadMore = (e) => {
    e.preventDefault();
    const nextPage = templatesError ? page : page + 1;

    if (templatesError || nextTokens[nextPage]) {
      resetListTemplateError(consultantDispatch);
      setPage(nextPage);
      requestListConsultantTemplates(consultantDispatch, {
        page: nextPage,
        limit: 10,
        nextToken: nextTokens[nextPage],
      });
    }
  };

  const navigateToEbilling = () => {
    if (user.id && user.membership === "CONSULTANT") {
      navigate(`/ebilling/template?consultation_id=${consultation.id}`);
    }
  };

  const TemplateSkeleton = () => {
    const contentArray = [];

    for (let i = 0; i < 7; i++) {
      const content = (
        <div
          key={i}
          className="relative flex items-start py-2 px-4 cursor-pointer hover:bg-slate-100"
        >
          <div className="min-w-0 flex-1 text-sm animate-pulse">
            <p className="truncate bg-gray-300 h-3 w-24 rounded-md"></p>
          </div>
        </div>
      );
      contentArray.push(content);
    }

    return (
      <div className="divide-y divide-gray-200 col-span-12">
        {contentArray}
        <div className="flex justify-center items-center py-4 col-span-12 animate-pulse">
          <div className="bg-gray-300 h-3 w-24 rounded-md"></div>
        </div>
      </div>
    );
  };

  return (
    <div
      className={classNames(
        className,
        "relative flex flex-col bg-white divide-y divide-slate-900/10",
      )}
    >
      {renderFileModal()}
      {renderCancelUpload()}
      {renderReviewModal()}
      {renderConsultanReviewModal()}
      {consultationID && (
        <ModalConfirm
          open={showSuccessModal}
          setOpen={(open) => {
            setshowSuccessModal(open);
          }}
          title="Success"
          description={
            consultation?.rating
              ? "Your rating and review have been successfully changed."
              : "Your rating and review have been successfully submitted."
          }
          type="SUCCESS"
          onConfirm={() => {
            setshowReviewModal(false);
            setshowSuccessModal(false);
            clearUpdateConsultation(consultationDispatch);
            clearUpdateConsultationError(consultationDispatch);
          }}
        />
      )}
      <div className="flex justify-between items-center">
        <div className="px-4 h-14 flex flex-col justify-center">
          <p className="font-bold text-sm">
            {user.membership === "CONSULTANT"
              ? consultation?.description?.name
              : consultation?.consultant?.user?.name || t("Consultant")}
          </p>
          {consultation.status === "FINISHED" && (
            <span className="text-xs text-gray-500">
              {t("Consultation Ended")}
            </span>
          )}
          {consultation.status === "ONGOING" && (
            <span className="text-xs text-gray-500">
              {t("End at")}{" "}
              {dayjs(consultation.expiredAt).format("DD MMM • HH:mm")}
            </span>
          )}
        </div>
        {renderActions()}
      </div>
      {loading ? (
        <div className="animate-pulse flex-1 p-4 overflow-y-scroll">
          <div>
            <div className="flex flex-row">
              <div className="h-3 w-8 mt-2 mb-1 bg-gray-300 rounded-md"></div>
            </div>
            <div className="flex flex-row">
              <div className="h-10 w-48 mt-1 bg-gray-300 rounded-md"></div>
            </div>
            <div className="flex flex-row">
              <div className="h-10 w-40 mt-1 bg-gray-300 rounded-md"></div>
            </div>
          </div>
          <div>
            <div className="flex flex-row-reverse">
              <div className="h-3 w-8 mt-2 mb-1 bg-gray-300 rounded-md"></div>
            </div>
            <div className="flex flex-row-reverse">
              <div className="h-10 w-40 mt-1 bg-gray-300 rounded-md"></div>
            </div>
          </div>
          <div>
            <div className="flex flex-row">
              <div className="h-3 w-8 mt-2 mb-1 bg-gray-300 rounded-md"></div>
            </div>
            <div className="flex flex-row">
              <div className="h-10 w-40 mt-1 bg-gray-300 rounded-md"></div>
            </div>
          </div>
        </div>
      ) : (
        <>
          {error ? (
            <ErrorList
              className="flex-1"
              title="Error"
              message={error?.message || "Error fetching messages."}
              errors={error?.errors}
            />
          ) : (
            <>
              {messages.length > 0 ? (
                <div
                  onScroll={onScroll}
                  ref={messageListRef}
                  className="relative flex-1 overflow-y-scroll px-4 py-3 space-y-2"
                >
                  {messages.map((message, index) => {
                    const messageAuthor = message.user.id;
                    const isAuthor = messageAuthor === user.id;
                    let type = "";
                    if (message.action?.name === "CALL") {
                      type = "CALL";
                    }
                    if (message.action?.name === "CALL_JOIN") {
                      type = "CALL_JOIN";
                    }
                    if (message.action?.name === "CALL_LEAVE") {
                      type = "CALL_LEAVE";
                    }
                    if (message.action?.name === "EBILLING") {
                      type = "EBILLING";
                    }

                    return (
                      <div key={message.id}>
                        {index === 0 && nextToken && (
                          <div>
                            {nextError && (
                              <Alert
                                className="mb-2"
                                type="error"
                                message={
                                  nextError?.message ||
                                  "Error fetching more messages."
                                }
                                errors={nextError?.errors}
                              />
                            )}
                            <div
                              ref={loadMoreRef}
                              className={classNames(
                                pulseMessage === "load-more"
                                  ? "animate-pulse"
                                  : "",
                                "flex mb-2 justify-center",
                              )}
                            >
                              <button
                                disabled={nextLoading}
                                className="p-2 inline-flex items-center rounded-md sm:rounded-lg bg-slate-100 disabled:text-gray-300"
                                style={{ color: theme.primary }}
                                onClick={() => {
                                  setisLoadMore(true);
                                  loadMoreMessages();
                                }}
                              >
                                {nextLoading ? (
                                  <svg
                                    role="status"
                                    className="w-4 h-4 animate-spin mr-2"
                                    viewBox="0 0 100 101"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                    aria-hidden="true"
                                  >
                                    <path
                                      d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                                      fill="#E5E7EB"
                                    />
                                    <path
                                      d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                                      fill="currentColor"
                                    />
                                  </svg>
                                ) : (
                                  <ArrowUpIcon className="h-4 w-4 mr-2" />
                                )}
                                <span className="text-sm">
                                  {t("Load More Messages")}
                                </span>
                              </button>
                            </div>
                          </div>
                        )}
                        {message.showDate && (
                          <div className="flex mb-2 justify-center">
                            <Badge title={message.date} theme={theme} />
                          </div>
                        )}
                        <div
                          ref={(el) => {
                            if (messagesRequireRef[message.id]) {
                              messagesRef.current[message.id] = el;
                            }
                          }}
                          className={classNames(
                            isAuthor ? "justify-end" : "justify-start",
                            "flex",
                          )}
                        >
                          <ChatBubble
                            className={
                              pulseMessage === message.id ? "animate-pulse" : ""
                            }
                            message={message}
                            isAuthor={isAuthor}
                            type={type}
                            theme={theme}
                            onReply={(message) => {
                              setreplyTo(message);
                              inputRef.current?.focus();
                              seteditTo({});
                              setmessage("");
                            }}
                            onEdit={(message) => {
                              seteditTo(message);
                              inputRef.current?.focus();
                              setreplyTo({});
                              setmessage("");
                            }}
                            onClickRepliedMessage={(messageId) => {
                              if (messagesRef.current[messageId]) {
                                messagesRef.current[messageId].scrollIntoView({
                                  behavior: "smooth",
                                });
                                setpulseMessage(messageId);
                              } else if (nextToken && loadMoreRef.current) {
                                loadMoreRef.current.scrollIntoView({
                                  behavior: "smooth",
                                });
                                setpulseMessage("load-more");
                              }
                            }}
                            onImageLoad={onImageLoad}
                            withOptions={consultation.status === "ONGOING"}
                            onJoinCall={!isAuthor ? onJoinCall : undefined}
                          />
                        </div>
                        {index === messages.length - 1 && (
                          <div ref={endMessageRef}></div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <EmptyList className="flex-1" title="No Message" />
              )}
            </>
          )}
        </>
      )}
      <div>
        {user.membership === "CONSULTANT" &&
          consultation?.status === "FINISHED" &&
          !showReplyAfterFinished && (
            <div className="p-4 flex justify-center">
              <Button
                outline
                title={t("Reply After Finished")}
                onClick={() => setshowReplyAfterFinished(true)}
              />
            </div>
          )}
        {(consultation?.status === "ONGOING" ||
          (user.membership === "CONSULTANT" && showReplyAfterFinished)) && (
          <form
            className="flex items-end p-4 gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              onSubmit();
            }}
          >
            {showNewMessageButton && (
              <div className="absolute bottom-[5.2rem] left-0 w-full flex justify-center pointer-events-none">
                <button
                  type="Button"
                  onClick={() => {
                    endMessageRef.current.scrollIntoView({
                      behavior: "smooth",
                    });
                    setshowNewMessageButton(false);
                  }}
                  className="bg-slate-100 px-2 py-1 rounded-md flex justify-center items-center shadow-md pointer-events-auto"
                  style={{ color: theme.primary }}
                >
                  <ArrowDownIcon className="w-4 h-4 mr-2" />
                  <span className="text-sm">{t("New Message")}</span>
                </button>
              </div>
            )}
            <div className="flex-1 flex gap-2">
              <Popover className="relative inline-flex my-auto">
                {({ open }) => (
                  <>
                    <Transition
                      as={Fragment}
                      enter="transition ease-out duration-100"
                      enterFrom="transform opacity-0 scale-95"
                      enterTo="transform opacity-100 scale-100"
                      leave="transition ease-in duration-75"
                      leaveFrom="transform opacity-100 scale-100"
                      leaveTo="transform opacity-0 scale-95"
                    >
                      <PopoverPanel className="-translate-y-full absolute -top-2 left-0 mb-2 p-1 w-48 rounded-md border border-gray-300 bg-white focus:outline-none">
                        {({ close }) => (
                          <>
                            <div>
                              <label
                                htmlFor="photo-file"
                                className="relative flex items-center m-1 p-1 rounded-md hover:bg-slate-100"
                              >
                                <PhotographIcon className="w-4 h-4" />
                                <div className="ml-3 text-xs">
                                  {t("Photo or Video")}
                                </div>
                              </label>
                              <input
                                type="file"
                                name="photo-file"
                                accept="image/png, image/jpeg"
                                id="photo-file"
                                className="sr-only"
                                onChange={(e) => {
                                  let file = e.target.files[0];
                                  if (file.size > 4e6) {
                                    addLocalNotification(notificationDispatch, {
                                      id: "chat-file-limit-image",
                                      type: "error",
                                      title: "File size limit exceeded",
                                      body: "File size should be less than 4MB.",
                                    });
                                  } else {
                                    const fileName = extractFileName(file);
                                    setfile(
                                      new File([file], fileName, {
                                        type: file.type,
                                      }),
                                    );
                                    setshowFileModal(true);
                                  }
                                  close();
                                }}
                              />
                            </div>
                            <div>
                              <label
                                htmlFor="document-file"
                                className="relative flex items-center m-1 p-1 rounded-md hover:bg-slate-100"
                              >
                                <DocumentTextIcon className="w-4 h-4" />
                                <div className="ml-3 text-xs">
                                  {t("Document")}
                                </div>
                              </label>
                              <input
                                type="file"
                                name="document-file"
                                accept="application/pdf, application/msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document, application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-powerpoint, application/vnd.openxmlformats-officedocument.presentationml.presentation"
                                id="document-file"
                                className="sr-only"
                                onChange={(e) => {
                                  let file = e.target.files[0];
                                  if (file.size > 4e6) {
                                    addLocalNotification(notificationDispatch, {
                                      id: "chat-file-limit-image",
                                      type: "error",
                                      title: "File size limit exceeded",
                                      body: "File size should be less than 4MB.",
                                    });
                                  } else {
                                    const fileName = extractFileName(file);
                                    setfile(
                                      new File([file], fileName, {
                                        type: file.type,
                                      }),
                                    );
                                    setshowFileModal(true);
                                  }
                                  close();
                                }}
                              />
                            </div>
                            {user.membership === "CONSULTANT" && (
                              <div onClick={navigateToEbilling}>
                                <label
                                  htmlFor="e-billing"
                                  className="relative flex items-center m-1 p-1 rounded-md hover:bg-slate-100"
                                >
                                  <ReceiptTaxIcon className="w-4 h-4" />
                                  <div className="ml-3 text-xs">
                                    {t("E-Billing")}
                                  </div>
                                </label>
                              </div>
                            )}
                          </>
                        )}
                      </PopoverPanel>
                    </Transition>
                    {!editTo.text && (
                      <PopoverButton
                        disabled={sendLoading || uploading}
                        className="relative inline-flex items-center text-sm font-medium focus:z-10 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2"
                      >
                        <span className="sr-only">Attach file</span>
                        <PaperClipIcon
                          className="w-6 h-6"
                          style={{
                            color:
                              sendLoading || uploading
                                ? "#9CA3AF"
                                : theme.primary,
                          }}
                        />
                      </PopoverButton>
                    )}
                  </>
                )}
              </Popover>
              {user.membership === "CONSULTANT" && (
                <Popover className="relative inline-flex my-auto">
                  {({ open }) => (
                    <>
                      <Transition
                        as={Fragment}
                        enter="transition ease-out duration-100"
                        enterFrom="transform opacity-0 scale-95"
                        enterTo="transform opacity-100 scale-100"
                        leave="transition ease-in duration-75"
                        leaveFrom="transform opacity-100 scale-100"
                        leaveTo="transform opacity-0 scale-95"
                      >
                        <PopoverPanel className="-translate-y-full absolute -translate-x-12 md:-translate-x-12 lg:-translate-x-0 -top-2 mb-2 w-[250px] sm:w-[365px] rounded-md border border-gray-300 bg-white focus:outline-none overflow-hidden">
                          {({ close }) => {
                            return (
                              <>
                                <div className="flex flex-col divide-y divide-gray-200 font-medium text-gray-700 text-sm">
                                  <span className="py-2 text-center">
                                    {t("Select Template")}
                                  </span>

                                  {templatesLoading || templateNextLoading ? (
                                    <TemplateSkeleton />
                                  ) : (
                                    <div className="divide-y divide-gray-200 h-64 overflow-y-auto">
                                      {templatesError ? (
                                        <div className="p-4">
                                          <Alert
                                            type="error"
                                            message={
                                              templatesError?.message ||
                                              "Error fetch template."
                                            }
                                            errors={templatesError?.errors}
                                          />
                                        </div>
                                      ) : null}
                                      {templates ? (
                                        templates.map((template) => {
                                          return (
                                            <div
                                              key={template.id}
                                              className="relative flex items-start py-2 px-4 cursor-pointer hover:bg-slate-100"
                                              onClick={() => {
                                                fillTemplate(template.text);
                                                close();
                                                inputRef.current.focus();
                                              }}
                                            >
                                              <div className="min-w-0 flex-1 text-sm">
                                                <p className="truncate">
                                                  {template.text}
                                                </p>
                                              </div>
                                            </div>
                                          );
                                        })
                                      ) : (
                                        <div className="flex justify-center item-center font-medium text-gray-700 text-sm p-4">
                                          <p>{t("No Template")}</p>
                                        </div>
                                      )}

                                      {!templatesLoading &&
                                      isLastTokenNull(nextTokens) ? null : (
                                        <div className="flex justify-center items-center py-4 col-span-12">
                                          <button
                                            onClick={onLoadMore}
                                            className="text-blue-500 hover:underline"
                                          >
                                            {t("Load More")}
                                          </button>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                  <div className="p-4 flex justify-between gap-4 bg-gray-50 rounded-b-lg">
                                    <Button
                                      outline
                                      type="button"
                                      title="Cancel"
                                      onClick={() => {
                                        close();
                                      }}
                                    />
                                    <Button
                                      title="Manage Template"
                                      onClick={() =>
                                        navigate("/settings/template")
                                      }
                                    />
                                  </div>
                                </div>
                              </>
                            );
                          }}
                        </PopoverPanel>
                      </Transition>
                      <PopoverButton
                        disabled={sendLoading || uploading}
                        onClick={() => {
                          getConsultantTemplate();
                          setPage(1);
                        }}
                        className="relative inline-flex items-center text-sm font-medium focus:z-10 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2"
                      >
                        <span className="sr-only">Consultant Template</span>
                        <ChatIcon
                          className="w-6 h-6"
                          style={{
                            color:
                              sendLoading || uploading
                                ? "#9CA3AF"
                                : theme.primary,
                          }}
                        />
                      </PopoverButton>
                    </>
                  )}
                </Popover>
              )}
              <div className="flex-1 bg-slate-100 rounded-md sm:rounded-lg focus-within:ring-2 focus-within:ring-blue-500">
                {(replyTo.text ||
                  replyTo.image?.key ||
                  replyTo.document?.key) && (
                  <div className="relative bg-slate-200 rounded-md sm:rounded-lg text-xs mb-2 p-2">
                    <div className="flex justify-between items-start gap-2">
                      <div className="flex items-center">
                        {replyTo.document?.key ? (
                          <DocumentTextIcon
                            className="w-10 h-10"
                            style={{ color: theme.primary }}
                          />
                        ) : replyTo.image?.key ? (
                          replyTo.imageUrl ? (
                            <img
                              src={replyTo.imageUrl}
                              alt="reply"
                              className="w-14 h-14 rounded-md mr-2"
                            />
                          ) : (
                            <PhotographIcon
                              className="w-10 h-10"
                              style={{ color: theme.primary }}
                            />
                          )
                        ) : null}
                        <div>
                          <p
                            className="font-bold"
                            style={{ color: theme.primary }}
                          >
                            {replyTo.user.name}
                          </p>
                          <p className="break-all">
                            {replyTo.text ||
                              generateFileName(
                                replyTo.image?.key || replyTo.document?.key,
                              )}
                          </p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setreplyTo({});
                        }}
                      >
                        <XIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                )}
                {editTo.text && (
                  <div className="relative bg-slate-200 rounded-md sm:rounded-lg text-xs mb-2 p-2">
                    <div className="flex justify-between items-center">
                      <p
                        className="flex-1 font-bold"
                        style={{ color: theme.primary }}
                      >
                        {editTo.user.name}
                      </p>
                      <button
                        type="button"
                        onClick={() => {
                          seteditTo({});
                          setmessage("");
                          inputRef.current.focus();
                        }}
                      >
                        <XIcon className="h-4 w-4" />
                      </button>
                    </div>
                    <p>{editTo.text}</p>
                  </div>
                )}
                <div className="flex items-center">
                  <textarea
                    id="message-textarea"
                    ref={inputRef}
                    placeholder={t("Type a message")}
                    className="flex-1 text-sm rounded-lg bg-slate-100 resize-none border-none w-36 h-[38px] max-h-80 disabled:opacity-50 disabled:cursor-not-allowed px-3 py-2"
                    value={message}
                    onInput={handleInput}
                    onChange={(e) => setmessage(e.target.value)}
                    onKeyDown={handleKeyDown}
                    rows={1}
                    disabled={sendLoading || uploading}
                  />
                </div>
              </div>
            </div>
            <div>
              <button
                type="submit"
                disabled={message.trim() === "" || sendLoading || uploading}
                className="inline-flex justify-center items-center whitespace-nowrap px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2 disabled:bg-slate-100 disabled:text-slate-400 disabled:cursor-not-allowed"
                style={
                  message.trim() === "" || sendLoading || uploading
                    ? {}
                    : {
                        backgroundColor: theme.primary,
                        color: theme.primaryText,
                      }
                }
              >
                {sendLoading || uploading ? (
                  <svg
                    role="status"
                    className="w-5 h-5 animate-spin text-slate-400"
                    viewBox="0 0 100 101"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                  >
                    <path
                      d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                      fill="#E5E7EB"
                    />
                    <path
                      d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                      fill="currentColor"
                    />
                  </svg>
                ) : editTo.text ? (
                  <CheckIcon className="w-5 h-5" />
                ) : (
                  <ChevronRightIcon className="w-5 h-5" />
                )}
              </button>
            </div>
          </form>
        )}
        {consultation?.status === "FINISHED" &&
          paymentStatus?.prices &&
          !consultation?.scheduleID && (
            <div className="p-4 flex gap-4 rounded-b-lg shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)]">
              {user.membership === "CONSULTANT" ? (
                <Button
                  onClick={() => setshowConsultantReviewModal(true)}
                  className="w-full"
                  title={
                    !consultation.rating && !consultation.review
                      ? "No Review Yet"
                      : "View Review"
                  }
                  outline
                  disabled={!consultation.rating && !consultation.review}
                />
              ) : (
                <>
                  <Button
                    onClick={() => setshowReviewModal(true)}
                    className="w-full"
                    title={
                      !consultation.rating && !consultation.review
                        ? "Rate and Review"
                        : "Change Review"
                    }
                    outline
                    disabled={loading}
                  />
                  <Button
                    icon={PlusIcon}
                    onClick={onExtend}
                    className="w-full"
                    title="Extend Consultation"
                    outline
                    disabled={loading}
                  />
                </>
              )}
            </div>
          )}
      </div>
    </div>
  );
};

export default ChatWindow;
